/**
 * DPR Go-Live Fixes 6-12 — frontend surfaces.
 *   FIX 6  WeatherStrip   — daily weather header (saves via section/header)
 *   FIX 7  SitePhotos     — upload/caption/delete site photos (max 10, 10MB ea.)
 *   FIX 8  HindrancesPanel — read-only hindrances pulled into the DPR
 *   FIX 12 VisitorsPanel  — read-only visitor log pulled into the DPR
 */
import React, { useEffect, useRef, useState } from 'react';
import { CloudSun, Camera, Loader2, Trash2, Lock, AlertOctagon, UserCheck, Upload, CheckCircle2, AlertTriangle, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiPost, apiPut, apiDel, apiUpload } from '../lib/api';

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—');
const SKY = ['', 'Clear', 'Sunny', 'Cloudy', 'Overcast', 'Light rain', 'Heavy rain', 'Storm'];

// ────────────────────────── FIX 6 · Weather ──────────────────────────
export function WeatherStrip({ doc, projectId, date, locked, onSaved }) {
  const w = doc?.weather || {};
  const [form, setForm] = useState({
    morning: w.morning || '', afternoon: w.afternoon || '', evening: w.evening || '',
    rainfall_mm: w.rainfall_mm ?? '', max_temp_c: w.max_temp_c ?? '',
    min_temp_c: w.min_temp_c ?? '', weather_notes: w.weather_notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        rainfall_mm: form.rainfall_mm === '' ? null : parseFloat(form.rainfall_mm),
        max_temp_c: form.max_temp_c === '' ? null : parseFloat(form.max_temp_c),
        min_temp_c: form.min_temp_c === '' ? null : parseFloat(form.min_temp_c),
      };
      const res = await apiPost(`/dpr/${encodeURIComponent(projectId)}/${encodeURIComponent(date)}/section/header`, { weather: payload });
      onSaved(res);
      toast.success('Weather recorded');
    } catch { toast.error('Could not save weather'); } finally { setSaving(false); }
  };
  const Sel = ({ k, label }) => (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">{label}</label>
      <select value={form[k]} disabled={locked} onChange={(e) => set(k, e.target.value)} className="w-full border border-slate-200 rounded-sm text-sm px-2 py-2 bg-white" data-testid={`dpr-weather-${k}`}>
        {SKY.map((s) => <option key={s} value={s}>{s || '—'}</option>)}
      </select>
    </div>
  );
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4" data-testid="dpr-weather">
      <div className="flex items-center gap-2 mb-3 font-display font-bold text-sm uppercase tracking-wider text-slate-700">
        <CloudSun size={16} className="text-amber-500" /> Weather · Site Conditions
        {locked && <Lock size={12} className="text-slate-400" />}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Sel k="morning" label="Morning" />
        <Sel k="afternoon" label="Afternoon" />
        <Sel k="evening" label="Evening" />
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Rainfall (mm)</label>
          <input type="number" step="any" disabled={locked} value={form.rainfall_mm} onChange={(e) => set('rainfall_mm', e.target.value)} className="w-full border border-slate-200 rounded-sm text-sm px-2 py-2 tabular-nums" data-testid="dpr-weather-rainfall" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Max °C</label>
          <input type="number" step="any" disabled={locked} value={form.max_temp_c} onChange={(e) => set('max_temp_c', e.target.value)} className="w-full border border-slate-200 rounded-sm text-sm px-2 py-2 tabular-nums" data-testid="dpr-weather-max" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Min °C</label>
          <input type="number" step="any" disabled={locked} value={form.min_temp_c} onChange={(e) => set('min_temp_c', e.target.value)} className="w-full border border-slate-200 rounded-sm text-sm px-2 py-2 tabular-nums" data-testid="dpr-weather-min" />
        </div>
      </div>
      <input disabled={locked} value={form.weather_notes} onChange={(e) => set('weather_notes', e.target.value)} placeholder="Weather notes (e.g. work stopped 2–4 PM due to rain)" className="w-full border border-slate-200 rounded-sm text-sm px-3 py-2 mt-3" data-testid="dpr-weather-notes" />
      {!locked && (
        <div className="flex justify-end mt-3">
          <button onClick={save} disabled={saving} className="text-sm font-display font-semibold uppercase tracking-wider px-4 py-2 rounded-sm bg-[#3b4872] text-white disabled:bg-slate-300" data-testid="dpr-weather-save">
            {saving ? 'Saving…' : 'Save weather'}
          </button>
        </div>
      )}
    </div>
  );
}

// ────────────────────────── FIX 7 · Site photos ──────────────────────────
function PhotoCard({ dprId, photo, locked, onChanged }) {
  const [src, setSrc] = useState(null);
  const [caption, setCaption] = useState(photo.caption || '');
  useEffect(() => {
    let active = true; let objUrl;
    api.get(`/dpr/${dprId}/photos/${photo.id}/serve`, { responseType: 'blob' })
      .then((r) => { if (active) { objUrl = URL.createObjectURL(r.data); setSrc(objUrl); } })
      .catch(() => {});
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [dprId, photo.id]);
  const saveCaption = async () => {
    try { await apiPut(`/dpr/${dprId}/photos/${photo.id}`, { caption }); toast.success('Caption saved'); }
    catch { toast.error('Could not save caption'); }
  };
  const del = async () => {
    try { await apiDel(`/dpr/${dprId}/photos/${photo.id}`); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
  };
  return (
    <div className="border border-slate-200 rounded-sm overflow-hidden bg-slate-50" data-testid={`dpr-photo-${photo.id}`}>
      <div className="aspect-video bg-slate-200 flex items-center justify-center overflow-hidden">
        {src ? <img src={src} alt={caption || 'Site photo'} className="w-full h-full object-cover" /> : <Loader2 size={18} className="animate-spin text-slate-400" />}
      </div>
      <div className="p-2 space-y-1.5">
        <input value={caption} disabled={locked} onChange={(e) => setCaption(e.target.value)} onBlur={!locked ? saveCaption : undefined} placeholder="Caption…" className="w-full border border-slate-200 rounded-sm text-xs px-2 py-1" data-testid={`dpr-photo-caption-${photo.id}`} />
        <div className="flex items-center justify-between text-[10px] text-slate-400">
          <span>{(photo.uploaded_by || '').split('@')[0]}</span>
          {!locked && <button onClick={del} className="text-rose-600 inline-flex items-center gap-0.5" data-testid={`dpr-photo-delete-${photo.id}`}><Trash2 size={11} /> Delete</button>}
        </div>
      </div>
    </div>
  );
}

// Verification badge next to the Site Photos title — driven by doc.photo_verification.
function PhotoVerifyBadge({ verification }) {
  const [open, setOpen] = useState(false);
  const status = verification?.status;
  if (status === 'ok') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-sm" data-testid="dpr-photo-verify-badge-ok">
        <CheckCircle2 size={12} /> Photos verified
      </span>
    );
  }
  if (status === 'mismatch') {
    const x = verification.ai_avg_pct ?? '—';
    const y = verification.reported_pct ?? '—';
    return (
      <span className="relative">
        <button
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-300 px-2 py-1 rounded-sm hover:bg-amber-100"
          data-testid="dpr-photo-verify-badge-mismatch"
          title="AI progress estimate differs from reported — click for detail"
        >
          <AlertTriangle size={12} /> Progress mismatch — AI ~{x}% vs {y}% reported. Review before finalising.
          <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="absolute z-10 mt-1 left-0 w-72 bg-white border border-amber-300 rounded-sm shadow-lg p-3 text-xs text-slate-700 space-y-1" data-testid="dpr-photo-verify-detail">
            <div className="font-display font-bold uppercase tracking-wider text-amber-800">Photo verification</div>
            <div>AI visible progress: <b>{x}%</b></div>
            <div>Self-reported progress: <b>{y}%</b></div>
            <div>Variance: <b className="text-amber-800">{verification.variance_pct > 0 ? '+' : ''}{verification.variance_pct}pt</b> (threshold ±20pt)</div>
            <div className="text-slate-400 pt-1">Run the AI Photo Verification panel below for the per-photo breakdown.</div>
          </div>
        )}
      </span>
    );
  }
  return null; // 'pending' / none → silent
}

export function SitePhotos({ doc, locked, onChanged }) {
  const photos = doc?.photos || [];
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  if (!doc?.id) return null;
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > 10) { toast.error('A DPR can hold at most 10 photos.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      await apiUpload(`/dpr/${doc.id}/photos`, fd);
      toast.success(`${files.length} photo(s) uploaded · AI verifying…`);
      onChanged();
      // Auto-verify is debounced ~2s server-side — re-fetch to pick up the result.
      setTimeout(() => onChanged(), 5000);
    } catch (e2) { toast.error(e2?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4" data-testid="dpr-photos">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="font-display font-bold text-sm uppercase tracking-wider text-slate-700 inline-flex items-center gap-2">
          <Camera size={16} className="text-[#3b4872]" /> Site Photos
          <span className="text-[11px] font-normal text-slate-400">({photos.length}/10)</span>
        </div>
        <PhotoVerifyBadge verification={doc.photo_verification} />
        {!locked && (
          <button onClick={() => inputRef.current?.click()} disabled={uploading || photos.length >= 10} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider rounded-sm bg-[#3b4872] text-white hover:bg-[#2c365a] disabled:opacity-50" data-testid="dpr-photos-upload-btn">
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {uploading ? 'Uploading…' : 'Upload'}
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid="dpr-photos-input" />
      </div>
      {photos.length === 0 ? (
        <div className="text-[12px] text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-sm">No site photos yet{!locked ? ' — upload up to 10 (max 10MB each).' : '.'}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="dpr-photos-grid">
          {photos.map((p) => <PhotoCard key={p.id} dprId={doc.id} photo={p} locked={locked} onChanged={onChanged} />)}
        </div>
      )}
    </div>
  );
}

// ────────────────── FIX 8 / 12 · read-only hindrances + visitors ──────────────────
export function HindrancesPanel({ doc }) {
  const rows = doc?.hindrances_today || [];
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4" data-testid="dpr-hindrances">
      <div className="flex items-center gap-2 mb-3 font-display font-bold text-sm uppercase tracking-wider text-slate-700">
        <AlertOctagon size={16} className="text-rose-500" /> Hindrances Today
        <span className="text-[11px] font-normal text-slate-400">(read-only · from Hindrance Register)</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-[12px] text-slate-400">No hindrances logged for this date.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full dense-table text-xs" data-testid="dpr-hindrances-table">
            <thead><tr><th>Type</th><th>Description</th><th className="text-right">Hours lost</th><th>Attributed to</th><th>Status</th></tr></thead>
            <tbody>
              {rows.map((h, i) => (
                <tr key={h.id || i} data-testid={`dpr-hindrance-row-${i}`}>
                  <td>{h.type || '—'}</td>
                  <td className="max-w-[280px] truncate">{h.description || h.title || '—'}</td>
                  <td className="text-right tabular-nums">{h.hours_lost ?? '—'}</td>
                  <td>{h.attributed_to_party || '—'}</td>
                  <td><span className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{h.status || '—'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function VisitorsPanel({ doc }) {
  const rows = doc?.visitors_today || [];
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4" data-testid="dpr-visitors">
      <div className="flex items-center gap-2 mb-3 font-display font-bold text-sm uppercase tracking-wider text-slate-700">
        <UserCheck size={16} className="text-emerald-600" /> Visitors Today
        <span className="text-[11px] font-normal text-slate-400">(read-only · from Visitor Log)</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-[12px] text-slate-400">No visitors checked in for this date.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full dense-table text-xs" data-testid="dpr-visitors-table">
            <thead><tr><th>Name</th><th>Company</th><th>Purpose</th><th>In</th><th>Out</th></tr></thead>
            <tbody>
              {rows.map((v, i) => (
                <tr key={v.id || i} data-testid={`dpr-visitor-row-${i}`}>
                  <td>{v.visitor_name || '—'}</td>
                  <td>{v.company || '—'}</td>
                  <td className="max-w-[240px] truncate">{v.purpose || '—'}</td>
                  <td className="tabular-nums">{fmtTime(v.checked_in_at)}</td>
                  <td className="tabular-nums">{fmtTime(v.checked_out_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
